import type { Teacher } from "../api/types";

export const CAPABILITIES = [
  "classes.switch",
  "classes.manage",
  "roster.write",
  "points.write",
  "history.manage",
  "monthly.publish",
  "share.manage",
  "academic.write",
  "counseling.write",
  "growth.write",
  "goals.write",
  "attendance.write",
  "teachers.manage",
  "presets.manage",
  "data.manage",
  "audit.view",
  "maintenance.manage",
  "admin.manage",
  "seating.write", "rewards.manage", "rewards.redeem"
] as const;

export type Capability = (typeof CAPABILITIES)[number];
export type TeacherRole = Teacher["role"];

const classroomWrite: Capability[] = [
  "seating.write", "rewards.redeem",
  "classes.switch",
  "roster.write",
  "points.write",
  "history.manage",
  "monthly.publish",
  "share.manage",
  "academic.write",
  "counseling.write",
  "growth.write",
  "goals.write",
  "attendance.write"
];

export const FALLBACK_ROLE_CAPABILITIES: Record<TeacherRole, ReadonlySet<Capability>> = {
  viewer: new Set(["classes.switch"]),
  teacher: new Set(classroomWrite),
  org_admin: new Set([...classroomWrite, "classes.manage", "teachers.manage", "rewards.manage"]),
  owner: new Set(CAPABILITIES)
};

export function can(teacher: Teacher | null | undefined, capability: Capability) {
  if (!teacher) return false;
  if (teacher.capabilities) return teacher.capabilities.includes(capability);
  return FALLBACK_ROLE_CAPABILITIES[teacher.role]?.has(capability) ?? false;
}

export function hasRole(teacher: Teacher | null | undefined, role: TeacherRole) {
  return teacher?.role === role;
}

export function canEditTeacher(actor: Teacher | null | undefined, target: Teacher) {
  if (!actor) return false;
  return hasRole(actor, "owner") || Number(actor.id) === Number(target.id) || !["owner", "org_admin"].includes(target.role);
}

export function canResetTeacherPassword(actor: Teacher | null | undefined, target: Teacher) {
  if (!actor || Number(actor.id) === Number(target.id)) return false;
  return hasRole(actor, "owner") || !["owner", "org_admin"].includes(target.role);
}

export function canToggleTeacher(actor: Teacher | null | undefined, target: Teacher) {
  if (!actor || Number(actor.id) === Number(target.id) || hasRole(target, "owner")) return false;
  return hasRole(actor, "owner") || !hasRole(target, "org_admin");
}

export function canChangeTeacherRole(actor: Teacher | null | undefined, target: Teacher) {
  if (!actor || hasRole(target, "owner")) return false;
  return hasRole(actor, "owner") || !hasRole(target, "org_admin");
}
